import type { CustomerSummary, DuplicatePairView } from "@kopi/contracts";
import { Badge } from "@kopi/ui/components/badge";
import { Button } from "@kopi/ui/components/button";
import { ConfirmDialog } from "@kopi/ui/components/confirm-button";
import { Skeleton } from "@kopi/ui/components/skeleton";
import { toast } from "@kopi/ui/components/sonner";
import { useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { LoadMore } from "@/components/infinite-list";
import { Page, PageHeader } from "@/components/page-header";
import { customerError } from "@/customers/customer-dialog";
import { customersKey, duplicatesQuery } from "@/customers/queries";
import { customers } from "@/i18n/messages/customers";
import { useI18n, useMessages } from "@/i18n/provider";
import { api, unwrap } from "@/lib/api";

export const Route = createFileRoute("/_authed/customers/duplicates")({
  staticData: {
    section: "workspace",
    parent: { label: "nav.customers", to: "/customers" },
    crumb: "nav.duplicates",
  },
  component: DuplicatesPage,
});

const pairKey = (pair: DuplicatePairView) => pair.customers.map((c) => c.id).join(":");

function Side(props: { customer: CustomerSummary }) {
  const { customer } = props;
  const { t } = useMessages(customers);
  const { format } = useI18n();
  return (
    <div className="flex min-w-0 flex-col gap-1 rounded-lg bg-muted/50 p-3 text-sm">
      <Link
        to="/customers/$customerId"
        params={{ customerId: customer.id }}
        className="truncate font-medium hover:underline"
      >
        {customer.displayName}
      </Link>
      <span className="truncate tabular-nums">{customer.phone ?? "—"}</span>
      <span className="truncate text-muted-foreground">{customer.email ?? "—"}</span>
      <span className="text-muted-foreground text-xs">
        {t("customers.since")} {format.date(new Date(customer.createdAt))}
        {customer.marketingConsent && ` · ${t("customers.consents")}`}
      </span>
    </div>
  );
}

/**
 * The duplicate review (R1-20): pairs of customers sharing a phone, an email or a name. A manager keeps
 * one record (the other merges into it, history and all) or marks the pair as different people.
 */
function DuplicatesPage() {
  const { t } = useMessages(customers);
  const queryClient = useQueryClient();
  const query = useInfiniteQuery(duplicatesQuery);
  const pairs = query.data?.pages.flatMap((page) => page.items) ?? [];
  const [merging, setMerging] = useState<{ keep: CustomerSummary; other: CustomerSummary } | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const done = async (message: string) => {
    await queryClient.invalidateQueries({ queryKey: customersKey });
    toast.success(message);
  };
  const merge = useMutation({
    mutationFn: async (choice: { keep: CustomerSummary; other: CustomerSummary }) =>
      unwrap(
        await api.v1.customers({ customerId: choice.keep.id }).merge.post({
          sourceCustomerId: choice.other.id,
          expectedRevision: choice.keep.revision,
          sourceRevision: choice.other.revision,
        }),
      ),
    onSuccess: () => done(t("customers.merged")),
    onError: (error) => {
      toast.error(customerError(t, error));
      void queryClient.invalidateQueries({ queryKey: customersKey });
    },
  });
  const dismiss = useMutation({
    mutationFn: async (pair: DuplicatePairView) =>
      unwrap(
        await api.v1.customers.duplicates.dismiss.post({
          customerIds: [pair.customers[0].id, pair.customers[1].id],
        }),
      ),
    onSuccess: () => done(t("customers.dismissed")),
    onError: (error) => toast.error(customerError(t, error)),
  });
  const busy = merge.isPending || dismiss.isPending;

  return (
    <Page>
      <PageHeader title={t("customers.duplicates")} subtitle={t("customers.dupSubtitle")} />
      {query.isPending ? (
        <div className="flex flex-col gap-3" aria-hidden>
          <Skeleton className="h-40 rounded-xl" />
          <Skeleton className="h-40 rounded-xl" />
        </div>
      ) : query.isError ? (
        <div className="flex flex-col items-start gap-2 text-sm">
          <p role="alert">{t("customers.dupLoadFailed")}</p>
          <Button type="button" variant="outline" onClick={() => void query.refetch()}>
            {t("customers.retry")}
          </Button>
        </div>
      ) : pairs.length === 0 ? (
        <p className="rounded-xl border border-dashed p-8 text-center text-muted-foreground text-sm">
          {t("customers.dupEmpty")}
        </p>
      ) : (
        <ul className="flex flex-col gap-3" aria-label={t("customers.duplicates")}>
          {pairs.map((pair) => {
            const [a, b] = pair.customers;
            return (
              <li
                key={pairKey(pair)}
                className="flex flex-col gap-3 rounded-xl border bg-card p-4"
                data-testid="duplicate-pair"
              >
                <div className="flex flex-wrap gap-2">
                  {pair.reasons.map((reason) => (
                    <Badge key={reason} variant="warning">
                      {t(`customers.reason.${reason}`)}
                    </Badge>
                  ))}
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  <Side customer={a} />
                  <Side customer={b} />
                </div>
                <div className="flex flex-wrap gap-2">
                  {[
                    { keep: a, other: b },
                    { keep: b, other: a },
                  ].map((choice) => (
                    <Button
                      key={choice.keep.id}
                      type="button"
                      variant="outline"
                      disabled={busy}
                      onClick={() => {
                        setMerging(choice);
                        setConfirmOpen(true);
                      }}
                    >
                      {t("customers.keep", { name: choice.keep.displayName })}
                    </Button>
                  ))}
                  <Button
                    type="button"
                    variant="ghost"
                    className="sm:ml-auto"
                    disabled={busy}
                    onClick={() => dismiss.mutate(pair)}
                  >
                    {t("customers.notDuplicate")}
                  </Button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
      <LoadMore pages={query} />
      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title={t("customers.mergeTitle", { name: merging?.keep.displayName ?? "" })}
        description={t("customers.mergeHint", {
          name: merging?.keep.displayName ?? "",
          other: merging?.other.displayName ?? "",
        })}
        confirmLabel={t("customers.merge")}
        onConfirm={() => merging && merge.mutate(merging)}
      />
    </Page>
  );
}
